import React from "react";
import "./CTApplications.css";
import PageHeader from "../../components/headers/PageHeader";
import wave from "../../assets/Images/waves/mix_wave.jpg";

export default function CTApplications() {
    return (
        <div>
            <PageHeader
                mediaSrc={wave}
                mediaType={"image"}
                // subtitle={"Clinical Operations Excellence"}
                title={"Clinical Trial Application with Clinical Studies - Operations"}
            />

            {/* Intro */}
            <section className="ct-section ct-introSection">
                <h2>
                    End-to-end clinical operations support for efficient,
                    compliant, and inspection-ready studies
                </h2>
                <p>
                    Our Clinical Operations team supports biopharma sponsors with
                    end-to-end execution capabilities designed to improve study
                    start-up speed, enhance site performance, and ensure
                    inspection-ready quality across all phases of clinical
                    development.
                </p>
                <p>
                    We combine operational expertise with data-driven oversight,
                    risk-based monitoring strategies, and integrated collaboration
                    across clinical, safety, regulatory, and biometrics functions.
                </p>
            </section>

            {/* Section 1 */}
            <section className="ct-section ct-cardSection">
                <h2>Clinical Site Identification, Feasibility and Study Start-Up</h2>

                <p className="ct-sectionIntro">
                    Our approach focuses on reducing start-up cycle times while
                    improving site selection quality and long-term enrolment
                    performance.
                </p>

                <div className="ct-cardRow">
                    <article className="ct-card">
                        <ul className="ct-cardList">
                            <li>Protocol feasibility assessment</li>
                            <li>Country and site selection strategy</li>
                            <li>Site feasibility surveys and qualification</li>
                            <li>Budget and contract support</li>
                            <li>Regulatory and ethics submission support</li>
                            <li>Site activation planning and tracking</li>
                        </ul>
                    </article>

                    <article className="ct-card">
                        <ul className="ct-cardList ct-cardList--secondary">
                            <li>Structured feasibility planning</li>
                            <li>Start-up workflow coordination</li>
                            <li>Site activation readiness tracking</li>
                            <li>Operational alignment with sponsor timelines</li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* Section 2 */}
            <section className="ct-section ct-cardSection">
                <h2>Site Management and Monitoring</h2>

                <p className="ct-sectionIntro">
                    Our monitoring strategy emphasizes data quality, patient
                    safety, and protocol compliance — not just visit execution.
                </p>

                <div className="ct-cardRow">
                    <article className="ct-card">
                        <ul className="ct-cardList">
                            <li>On-site monitoring — CRA support</li>
                            <li>Remote monitoring</li>
                            <li>Hybrid monitoring models</li>
                            <li>Site training and initiation visits</li>
                        </ul>
                    </article>

                    <article className="ct-card">
                        <ul className="ct-cardList ct-cardList--secondary">
                            <li>Source data verification strategy</li>
                            <li>Protocol deviation management</li>
                            <li>Investigator site communication and oversight</li>
                            <li>Study-specific monitoring model alignment</li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* Section 3 */}
            <section className="ct-section ct-cardSection">
                <h2>Risk-Based Quality Management — RBQM</h2>

                <p className="ct-sectionIntro">
                    We embed risk-based oversight across the entire study lifecycle
                    to enable proactive issue detection and mitigation.
                </p>

                <div className="ct-cardRow">
                    <article className="ct-card">
                        <ul className="ct-cardList">
                            <li>Critical data and process identification</li>
                            <li>Key Risk Indicator — KRI development and monitoring</li>
                            <li>Centralized monitoring dashboards</li>
                            <li>Risk signal detection and escalation pathways</li>
                            <li>Issue trending and corrective action planning</li>
                            <li>Oversight of vendor and CRO performance</li>
                            <li>
                                In-house AI-based RBQM tool{" "}
                                <a href="/products" className="ct-inlineLink">
                                    Nebula™
                                </a>
                            </li>
                        </ul>
                    </article>

                    <article className="ct-card">
                        <p className="ct-cardIntro">
                            Our RBQM approach reduces reliance on traditional 100%
                            SDV models and enables smarter, targeted monitoring.
                            Eupraxia’s RBQM approach makes us a unique organization
                            using structured data and analytics to improve trial
                            execution performance.
                        </p>

                        <ul className="ct-cardList ct-cardList--secondary">
                            <li>Enrolment rate tracking and forecasting</li>
                            <li>Site performance analytics</li>
                            <li>Patient dropout and retention modelling</li>
                            <li>Protocol deviation trend analysis</li>
                            <li>Operational risk dashboards</li>
                            <li>Cross-study benchmarking insights</li>
                        </ul>
                    </article>
                </div>

                <p className="ct-sectionOutro">
                    This enables sponsors to move from reactive trial management
                    to proactive execution control.
                </p>
            </section>

            {/* Section 4 */}
            <section className="ct-section ct-cardSection">
                <h2>Medical Monitoring, Clinical Science and Medical Writing</h2>

                <p className="ct-sectionIntro">
                    Integrated medical oversight is embedded into our clinical
                    operations model to ensure patient safety and protocol
                    integrity.
                </p>

                <div className="ct-cardRow">
                    <article className="ct-card">
                        <ul className="ct-cardList">
                            <li>Eligibility review support</li>
                            <li>Medical review of adverse events</li>
                            <li>Protocol deviation assessment</li>
                        </ul>
                    </article>

                    <article className="ct-card">
                        <ul className="ct-cardList ct-cardList--secondary">
                            <li>Dose escalation support for early phase studies</li>
                            <li>
                                Clinical interpretation support for investigators
                                and sponsors
                            </li>
                            <li>
                                Safety signal escalation coordination with
                                pharmacovigilance teams
                            </li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* Section 5 */}
            <section className="ct-section ct-cardSection">
                <h2>Clinical Supply Chain Management</h2>

                <p className="ct-sectionIntro">
                    Our clinical trial supply chain management team is capable of
                    delivering comprehensive, end-to-end clinical supply chain
                    management services across study sites — with a focus on timely
                    delivery, continuity, and operational control.
                </p>
            </section>

            {/* Section 6 */}
            <section className="ct-section ct-cardSection">
                <h2>Clinical Trial Management Support</h2>

                <p className="ct-sectionIntro">
                    Our trial management support strengthens study governance,
                    improves transparency, and keeps clinical execution aligned
                    with sponsor objectives.
                </p>

                <div className="ct-cardRow">
                    <article className="ct-card">
                        <ul className="ct-cardList">
                            <li>Study tracking and milestone management</li>
                            <li>Vendor oversight and coordination</li>
                            <li>TMF planning and quality oversight</li>
                        </ul>
                    </article>

                    <article className="ct-card">
                        <ul className="ct-cardList ct-cardList--secondary">
                            <li>Trial Master File — TMF inspection readiness</li>
                            <li>Cross-functional communication management</li>
                            <li>Study status reporting and dashboards</li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* Section 7 */}
            <section className="ct-section ct-cardSection">
                <h2>Data Management and Biometrics</h2>

                <p className="ct-sectionIntro">
                    Our clinical operations model works closely with data
                    management, statistics, and inference teams to support accurate
                    data flow, structured oversight, and reliable clinical
                    decision-making.
                </p>

                <p className="ct-noteText">
                    See also: CDISC Standardization, Data Management, Statistics
                    and Data Inference.
                </p>
            </section>

            {/* Final Section */}
            <section className="ct-section ct-cardSection">
                <h2>Integrated Clinical Development Model</h2>

                <p className="ct-sectionIntro">
                    Unlike traditional CRO structures, our Clinical Operations
                    team works in close integration with specialized scientific,
                    regulatory, clinical, and data functions.
                </p>

                <ul className="ct-plainList">
                    <li>Pharmacovigilance &amp; Drug Safety</li>
                    <li>Medical Monitoring</li>
                    <li>Biostatistics &amp; Data Sciences</li>
                    <li>Regulatory Affairs</li>
                    <li>SEND and Nonclinical Data Teams for translational alignment</li>
                </ul>

                <p className="ct-sectionOutro">
                    This integrated model ensures consistency from nonclinical
                    development through clinical execution and regulatory
                    submission.
                </p>
            </section>
        </div>
    );
}
